import {
  ComponentType,
  createElement,
  FC,
  lazy,
  Suspense,
  useMemo,
} from 'react';

import { TFunction } from 'i18next';

import Api from '~/Api';
import Loader from '~/components/atoms/Loader';
import { PaymentApi } from '~/components/organisms/SolidCheckout/types';
import SolidCheckout, {
  paymentFormModalConfig,
} from '~/components/organisms/SolidCheckout/views/Checkout';
import { PRODUCTS_PROJECT_CONFIG } from '~/constants/products';
import type { ModalStateType } from '~/contexts/modal/ModalContext';
import type {
  CancellationFormDataType,
  FormFields,
} from '~/modules/cancellation/types';
import {
  ComplianceExperienceId,
  parseComplianceExperienceId,
} from '~/services/compliance/experienceId';
import { useAppSelector } from '~/store/hooks/useAppSelector';
import { colors } from '~/theme/colors';
import { ProductType } from '~/types/product';

export type SubscriptionEntryProps = {
  CurrentSubscriptionEntry: FC;
  LegacySubscriptionEntry: FC;
};

export type SubscriptionUpgradeBannerProps = {
  product: ProductType;
  onUpgradeClick: () => Promise<void>;
};

export type SubscriptionUpgradePaywallProps = {
  product: ProductType;
  onSuccessPayment: () => void | Promise<void>;
  paymentApi?: PaymentApi;
  purchaseSource?: string;
};

export type RestoreSubscriptionConfig = {
  products: ProductType[];
  Paywall: FC<SubscriptionUpgradePaywallProps>;
  paywallModalConfig: Partial<ModalStateType>;
};

export type EntryFomoModalProps = {
  subscriptionName: string;
  onKeepPlan: () => void;
  onCancelPlan: () => void;
};

export type CancellationFormProps = {
  title: string;
  onFormSubmit: (data: CancellationFormDataType) => Promise<void>;
  onFormRefuse: () => void;
};

export type PostCancelFeedbackProps = {
  title: string;
  placeholder?: string;
  onSubmit: (
    data: Pick<CancellationFormDataType, FormFields.FEEDBACK>,
  ) => Promise<void>;
  onRefuse: () => void;
  onProductSwitch?: () => Promise<void>;
};

export type SuccessProps = {
  onContinueToFeedback: () => void;
  nextButtonText: string;
};

export type ComplianceExperience = {
  id: ComplianceExperienceId;
  subscription: {
    Entry: FC<SubscriptionEntryProps>;
    EntryFomoModal?: FC<EntryFomoModalProps>;
    UpgradeBanner: FC<SubscriptionUpgradeBannerProps>;
    UpgradePaywall?: FC<SubscriptionUpgradePaywallProps>;
    upgradePaywallModalConfig?: Partial<ModalStateType>;
    signUpSubscription: {
      products: ProductType[];
      showProductDescription: boolean;
      Paywall: FC<SubscriptionUpgradePaywallProps>;
      paywallModalConfig: Partial<ModalStateType>;
    };
    restoreSubscription?: RestoreSubscriptionConfig;
  };
  cancellation: {
    Entry?: FC;
    Form?: FC<CancellationFormProps>;
    Success?: FC<SuccessProps>;
    PostCancelFeedback?: FC<PostCancelFeedbackProps>;
  };
  billing: {
    historyEnabled: boolean;
  };
  home: {
    getToolkitTitle: (t: TFunction) => string;
  };
};

const CurrentSubscriptionEntry: FC<SubscriptionEntryProps> = ({
  CurrentSubscriptionEntry: Entry,
}) => createElement(Entry);

const LegacySubscriptionEntry: FC<SubscriptionEntryProps> = ({
  LegacySubscriptionEntry: Entry,
}) => createElement(Entry);

const lazyRegisteredComponent = <Props extends object>(
  loader: () => Promise<{ default: FC<Props> }>,
): FC<Props> => {
  const Component = lazy(loader);

  const RegisteredComponent: FC<Props> = (props) => {
    const ComponentWithProps = Component as unknown as ComponentType<Props>;

    return createElement(
      Suspense,
      { fallback: createElement(Loader) },
      createElement(ComponentWithProps, props),
    );
  };

  return RegisteredComponent;
};

const lazyRegisteredPage = (loader: () => Promise<{ default: FC }>): FC => {
  const Component = lazy(loader);

  const RegisteredPage: FC = () =>
    createElement(
      Suspense,
      { fallback: createElement(Loader) },
      createElement(Component),
    );

  return RegisteredPage;
};

const V0EntryFomoModal = lazyRegisteredComponent<EntryFomoModalProps>(() =>
  import('./components').then((module) => ({
    default: module.EntryFomoModal,
  })),
);

const V1EntryFomoModal = V0EntryFomoModal;

const DefaultSubscriptionUpgradeBanner =
  lazyRegisteredComponent<SubscriptionUpgradeBannerProps>(() =>
    import('./components').then((module) => ({
      default: module.DefaultSubscriptionUpgradeBanner,
    })),
  );

const V2SubscriptionUpgradeBanner =
  lazyRegisteredComponent<SubscriptionUpgradeBannerProps>(() =>
    import('./components').then((module) => ({
      default: module.V2SubscriptionUpgradeBanner,
    })),
  );

const V2SubscriptionUpgradePaywall =
  lazyRegisteredComponent<SubscriptionUpgradePaywallProps>(() =>
    import('./components').then((module) => ({
      default: module.V2SubscriptionUpgradePaywall,
    })),
  );

const v2SubscriptionUpgradePaywallModalConfig: Partial<ModalStateType> = {
  isFullScreen: false,
  maxWidth: '480px',
  paddingSize: '0px',
  closeButtonColor: colors.neutral_30,
  pageViewMobile: true,
};

const LegacySignUpSubscriptionPaywall: FC<SubscriptionUpgradePaywallProps> = ({
  product,
  onSuccessPayment,
  paymentApi,
  purchaseSource,
}) => {
  const { id } = useAppSelector((state) => state.profile);
  const checkoutPaymentApi = useMemo(() => {
    if (!paymentApi) {
      return {
        createPaymentIntentCard: Api.createPaymentIntentCard.bind(Api),
        createPaymentIntentPaypal: Api.createPaymentIntentPaypal.bind(Api),
      };
    }

    const cardApi = {
      createPaymentIntentCard: (
        _userId: string,
        _productId: string,
        geo_country: string,
      ) => paymentApi.createPaymentIntentCard(geo_country),
    };

    if (!paymentApi.createPaymentIntentPaypal) {
      return cardApi;
    }

    return {
      ...cardApi,
      createPaymentIntentPaypal: (_userId: string, _productId: string) =>
        paymentApi.createPaymentIntentPaypal!(),
    };
  }, [paymentApi]);

  return createElement(SolidCheckout, {
    onSuccessPayment,
    paymentApi: checkoutPaymentApi,
    product,
    purchaseSource,
    userId: id,
  });
};

const getComplianceSignUpSubscriptionProducts = (): ProductType[] =>
  PRODUCTS_PROJECT_CONFIG.COMPLIANCE_SIGNUP_PRODUCTS;

const legacySignUpSubscription = {
  Paywall: LegacySignUpSubscriptionPaywall,
  paywallModalConfig: paymentFormModalConfig,
  products: PRODUCTS_PROJECT_CONFIG.SUB_NOT_FOUND_PRODUCTS,
  showProductDescription: false,
};

const complianceSignUpSubscription = {
  Paywall: V2SubscriptionUpgradePaywall,
  paywallModalConfig: v2SubscriptionUpgradePaywallModalConfig,
  products: getComplianceSignUpSubscriptionProducts(),
  showProductDescription: true,
};

const getRestoreSubscriptionProducts = (): ProductType[] =>
  PRODUCTS_PROJECT_CONFIG.COMPLIANCE_SIGNUP_PRODUCTS.filter(
    (product) => !product.has_trial_period,
  );

const complianceRestoreSubscription: RestoreSubscriptionConfig = {
  Paywall: V2SubscriptionUpgradePaywall,
  paywallModalConfig: v2SubscriptionUpgradePaywallModalConfig,
  products: getRestoreSubscriptionProducts(),
};

const getPersonalizedToolkitTitle = (t: TFunction): string =>
  t('general:personalized_toolkit', 'Personalized Toolkit');

const getToolkitTitle = (t: TFunction): string =>
  t('general:toolkit', 'Toolkit');

const V0CancellationEntry = lazyRegisteredPage(
  () => import('~/modules/cancellation/views/variants/v3'),
);

const V1CancellationForm = lazyRegisteredComponent<CancellationFormProps>(() =>
  import('./components').then((module) => ({
    default: module.V1CancellationForm,
  })),
);

const V2CancellationForm = lazyRegisteredComponent<CancellationFormProps>(() =>
  import('./components').then((module) => ({
    default: module.V2CancellationForm,
  })),
);

const StandardSuccess = lazyRegisteredComponent<SuccessProps>(() =>
  import('./components').then((module) => ({
    default: module.StandardSuccess,
  })),
);

const V2Success = lazyRegisteredComponent<SuccessProps>(() =>
  import('./components').then((module) => ({
    default: module.V2Success,
  })),
);

const V2PostCancelFeedback = lazyRegisteredComponent<PostCancelFeedbackProps>(
  () =>
    import('./components').then((module) => ({
      default: module.PostCancelFeedback,
    })),
);

export const COMPLIANCE_EXPERIENCE_REGISTRY: Readonly<
  Record<ComplianceExperienceId, ComplianceExperience>
> = {
  [ComplianceExperienceId.V0]: {
    id: ComplianceExperienceId.V0,
    subscription: {
      Entry: LegacySubscriptionEntry,
      EntryFomoModal: V0EntryFomoModal,
      UpgradeBanner: DefaultSubscriptionUpgradeBanner,
      signUpSubscription: legacySignUpSubscription,
    },
    cancellation: {
      Entry: V0CancellationEntry,
    },
    billing: {
      historyEnabled: false,
    },
    home: {
      getToolkitTitle: getPersonalizedToolkitTitle,
    },
  },
  [ComplianceExperienceId.V1]: {
    id: ComplianceExperienceId.V1,
    subscription: {
      Entry: CurrentSubscriptionEntry,
      EntryFomoModal: V1EntryFomoModal,
      UpgradeBanner: V2SubscriptionUpgradeBanner,
      UpgradePaywall: V2SubscriptionUpgradePaywall,
      upgradePaywallModalConfig: v2SubscriptionUpgradePaywallModalConfig,
      signUpSubscription: complianceSignUpSubscription,
      restoreSubscription: complianceRestoreSubscription,
    },
    cancellation: {
      Form: V1CancellationForm,
      Success: StandardSuccess,
    },
    billing: {
      historyEnabled: true,
    },
    home: {
      getToolkitTitle,
    },
  },
  [ComplianceExperienceId.V2]: {
    id: ComplianceExperienceId.V2,
    subscription: {
      Entry: CurrentSubscriptionEntry,
      UpgradeBanner: V2SubscriptionUpgradeBanner,
      UpgradePaywall: V2SubscriptionUpgradePaywall,
      upgradePaywallModalConfig: v2SubscriptionUpgradePaywallModalConfig,
      signUpSubscription: complianceSignUpSubscription,
      restoreSubscription: complianceRestoreSubscription,
    },
    cancellation: {
      Form: V2CancellationForm,
      Success: V2Success,
      PostCancelFeedback: V2PostCancelFeedback,
    },
    billing: {
      historyEnabled: true,
    },
    home: {
      getToolkitTitle,
    },
  },
};

export const getComplianceExperience = (value: unknown): ComplianceExperience =>
  COMPLIANCE_EXPERIENCE_REGISTRY[parseComplianceExperienceId(value)];

export { ComplianceExperienceId, parseComplianceExperienceId };
